import {
  type EntityDataType,
  type HighYoYReason,
  NO_REASON,
  type PortfolioDataInsightsReverseFieldMap,
} from '@netpurpose/types'
import { valueIsDefined } from '@netpurpose/utils'
import { pickBy } from 'lodash'
import type {
  EntityValues as EntityValuesBE,
  EntityWithOptionalDetailsAndSdgRevenuePct as EntityWithDetailsAndSDGRevenuePct,
  InsightResults,
  ReportingStandardMajorDetailsWithOrigin,
} from '../../../generated/facts'
import { transformSource } from '../../../models/source'
import type { ReverseFieldMap } from '../../../queryBuilder'
import type { Camelize } from '../../../utils'
import type { FactoidOrigin } from '../useImpactData'

export type AlignedSDG = Camelize<ReportingStandardMajorDetailsWithOrigin>
export type Entity = Omit<
  Camelize<EntityWithDetailsAndSDGRevenuePct>,
  'entityId' | 'alignedSdgs'
> & {
  id: number
  alignedSdgs: AlignedSDG[]
  themes: string[]
  sdiSegments?: string[] | undefined
  description?: string | undefined
}

export type EntityValues = Camelize<EntityValuesBE>

type EntityFilters = Entity & {
  withDetails?: boolean
  revenue?: number
  factsetId?: string
}

export const transformEntity = <
  EntityDataProps extends Camelize<EntityWithDetailsAndSDGRevenuePct>,
>({
  entityId,
  name,
  themes,
  isCovered,
  activeSince,
  inactiveSince,
  alignedSdgs,
  ...rest
}: EntityDataProps): Entity => ({
  ...pickBy(rest, valueIsDefined),
  id: entityId,
  name,
  themes: themes || [],
  isCovered,
  activeSince: activeSince ?? null,
  inactiveSince: inactiveSince ?? null,
  alignedSdgs: alignedSdgs
    ? alignedSdgs?.map((sdg) => ({
        standardClass: sdg.standardClass,
        ...pickBy(
          {
            id: parseInt(sdg.majorCode as string, 10),

            majorCode: sdg.majorCode,
            fullCode: undefined,
            description: undefined,
            link: sdg.link,
            colour: sdg.colour,
            origins: sdg.origins,
          },
          valueIsDefined,
        ),
      }))
    : [],
})

export const transformHighYoyReasons = (
  highYoyReasons: Camelize<InsightResults['high_yoy_reasons']>,
): HighYoYReason[] | undefined =>
  highYoyReasons
    ?.map((highYoyReason) => {
      if (
        highYoyReason.detail !== NO_REASON &&
        highYoyReason.reference &&
        valueIsDefined(highYoyReason.reference.pageNum)
      ) {
        return {
          variant: 'withReference' as const,
          factId: highYoyReason.factId || undefined,
          id: highYoyReason.factAnnotationId || undefined,
          // annotation_type, ie., High YoY variance, but we don't get it from this endpoint
          type: undefined,
          detail: highYoyReason.detail,
          sourceId: highYoyReason.reference.sourceId,
          pageNum: highYoyReason.reference.pageNum,
          referenceId: highYoyReason.reference.referenceId,
          metricName: highYoyReason.metricName || undefined,
          source: highYoyReason.reference?.source
            ? transformSource(highYoyReason.reference.source)
            : undefined,
        }
      } else if (highYoyReason.detail === NO_REASON) {
        return {
          variant: 'base' as const,
          id: highYoyReason.factAnnotationId || undefined,
          factId: highYoyReason.factId || undefined,
          // annotation_type, ie., High YoY variance, but we don't get it from this endpoint
          type: undefined,
          detail: highYoyReason.detail,
          metricName: highYoyReason.metricName || undefined,
        }
      }
      return undefined
    })
    .filter(valueIsDefined)

export type EntityWithQuestionDetails = Omit<
  Camelize<InsightResults>,
  | 'entityId'
  | 'isCovered'
  | 'themes'
  | 'dataOriginUrls'
  | 'dataType'
  | 'factoidOrigin'
  | 'isHighYoy'
  | 'highYoyReasons'
> & {
  id: number
  isCovered: boolean | undefined
  themes: string[]
  dataOriginUrls: string[]
  dataType: EntityDataType
  factoidOrigin: FactoidOrigin | undefined
  isHighYoY: boolean
  highYoYReasons: HighYoYReason[] | undefined
}

export type EntityWithQuestionDetailsFilters = EntityWithQuestionDetails &
  EntityFilters &
  PortfolioDataInsightsReverseFieldMap

export const transformEntityDataByQuestionId = <
  EntityDataByQuestionIdProps extends Camelize<InsightResults>,
>({
  entityId,
  factoidOrigin,
  dataType,
  isHighYoy,
  highYoyReasons,
  company,
  portfolio,
  ...rest
}: EntityDataByQuestionIdProps) => ({
  ...rest,
  id: entityId,
  dataType: (dataType === 'Calculated' ? 'Standardised' : dataType) as EntityDataType,
  factoidOrigin: factoidOrigin
    ? {
        ...factoidOrigin,
        factoidId: factoidOrigin.factoidId,
        description: factoidOrigin?.description,
        dataType: (factoidOrigin?.dataType === 'Calculated'
          ? 'Standardised'
          : factoidOrigin?.dataType) as EntityDataType,
      }
    : undefined,
  company,
  portfolio,
  isHighYoY: isHighYoy || false,
  highYoYReasons: transformHighYoyReasons(highYoyReasons),
})

export const reverseEntityWithQuestionDetailsFieldMap: ReverseFieldMap<
  keyof EntityWithQuestionDetails & PortfolioDataInsightsReverseFieldMap
> = {
  id: 'entity_id',
  name: 'name',
  isCovered: 'is_covered',
  primaryIsin: 'primary_isin',
  headquarters: 'headquarters',
  sector: 'sector',
  industry: 'industry',
  themes: 'themes',
  marketCap: 'market_cap',
  revenue: 'revenue',
  dataOriginUrls: 'data_origin_urls',
  dataType: {
    key: 'data_type',
    transform: (dataType: EntityDataType) =>
      dataType === 'Standardised' ? 'Calculated' : dataType,
  },
  highYoYReasons: 'high_yoy_reasons',
  isHighYoY: 'is_high_yoy',
  factoidOrigin: 'factoid_origin',
  alignedSdgs: 'aligned_sdgs',
  'company.impact': 'company_impact',
  'company.evicIntensity': 'company_evic_intensity',
  'company.revenueIntensity': 'company_revenue_intensity',
  'company.impactYoy': 'company_impact_yoy',
  'company.impactYoyDelta': 'company_impact_yoy_delta',
  'company.sdgRevenuePercentage': 'company_sdg_revenue_percentage',
  'portfolio.impact': 'portfolio_impact',
  'portfolio.evicIntensity': 'portfolio_evic_intensity',
  'portfolio.revenueIntensity': 'portfolio_revenue_intensity',
  'portfolio.impactYoy': 'portfolio_impact_yoy',
  'portfolio.sdgRevenuePercentage': 'portfolio_sdg_revenue_percentage',
}
